const DB_NAME = "hami-avatar-cache";
const STORE_NAME = "avatars";
const DB_VERSION = 1;

/** Profile thumbs change occasionally; 24h balances freshness and repeat visits. */
export const AVATAR_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_CACHE_BYTES = 15 * 1024 * 1024;
const MAX_CACHE_ITEMS = 400;

function isIndexedDbAvailable() {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("expiresAt", "expiresAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Failed to open avatar cache"));
  });
}

function runTransaction(mode, handler) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        let settled = false;
        const finishResolve = (value) => {
          if (!settled) {
            settled = true;
            resolve(value);
          }
        };
        const finishReject = (error) => {
          if (!settled) {
            settled = true;
            reject(error);
          }
        };
        tx.oncomplete = () => {
          db.close();
          if (!settled) finishResolve(undefined);
        };
        tx.onabort = () => {
          db.close();
          finishReject(tx.error || new Error("Avatar cache transaction aborted"));
        };
        tx.onerror = () => {
          db.close();
          finishReject(tx.error || new Error("Avatar cache transaction failed"));
        };
        try {
          handler(store, finishResolve, finishReject);
        } catch (error) {
          db.close();
          finishReject(error);
        }
      })
  );
}

function getRecordSize(record) {
  if (!record) return 0;
  if (typeof record.size === "number" && Number.isFinite(record.size)) return record.size;
  if (record.blob && typeof record.blob.size === "number") return record.blob.size;
  return 0;
}

async function getAllRecords() {
  return runTransaction("readonly", (store, resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error || new Error("Failed to read avatar cache"));
  });
}

async function deleteById(id) {
  if (!id) return false;
  try {
    await runTransaction("readwrite", (store, resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error("Failed to delete avatar cache record"));
    });
    return true;
  } catch {
    return false;
  }
}

async function evictRecords(recordsToDelete = []) {
  for (const record of recordsToDelete) {
    await deleteById(record?.id);
  }
}

async function ensureCacheCapacity(incomingSize = 0, incomingId = null) {
  const now = Date.now();
  const allRecords = await getAllRecords();

  const expiredRecords = allRecords.filter(
    (record) => record?.expiresAt && record.expiresAt <= now && record.id !== incomingId
  );
  if (expiredRecords.length > 0) {
    await evictRecords(expiredRecords);
  }

  const freshRecords = (await getAllRecords()).filter(
    (record) => !record?.expiresAt || record.expiresAt > now
  );
  const currentRecords = freshRecords.filter((record) => record.id !== incomingId);
  let totalBytes = currentRecords.reduce((sum, record) => sum + getRecordSize(record), 0);
  let totalItems = currentRecords.length;

  if (totalBytes + incomingSize <= MAX_CACHE_BYTES && totalItems + 1 <= MAX_CACHE_ITEMS) {
    return true;
  }

  const evictionCandidates = [...currentRecords].sort((a, b) => {
    const aAccess = a?.lastAccessedAt || a?.cachedAt || 0;
    const bAccess = b?.lastAccessedAt || b?.cachedAt || 0;
    return aAccess - bAccess;
  });

  const toDelete = [];
  for (const record of evictionCandidates) {
    if (totalBytes + incomingSize <= MAX_CACHE_BYTES && totalItems + 1 <= MAX_CACHE_ITEMS) {
      break;
    }
    toDelete.push(record);
    totalBytes -= getRecordSize(record);
    totalItems -= 1;
  }

  if (toDelete.length > 0) {
    await evictRecords(toDelete);
  }

  return totalBytes + incomingSize <= MAX_CACHE_BYTES && totalItems + 1 <= MAX_CACHE_ITEMS;
}

export function getAvatarCacheId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  return trimmed ? `avatar:${trimmed}` : null;
}

/** Every unexpired saved avatar, as [url, blob] — read once at start-up so known faces are there in the first frame. */
export async function getAllCachedAvatarBlobs() {
  try {
    const records = await runTransaction("readonly", (store, resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error || new Error("Failed to read avatar cache"));
    });
    const now = Date.now();
    return (records || [])
      .filter((r) => r?.blob && typeof r.id === "string" && r.id.startsWith("avatar:") && !(r.expiresAt && r.expiresAt <= now))
      .map((r) => [r.id.slice("avatar:".length), r.blob]);
  } catch {
    return [];
  }
}

/**
 * @param {string} url Normalized avatar URL (with thumb param).
 * @returns {Promise<Blob|null>}
 */
export async function getCachedAvatarBlob(url) {
  const cacheId = getAvatarCacheId(url);
  if (!cacheId) return null;
  try {
    const record = await runTransaction("readonly", (store, resolve, reject) => {
      const req = store.get(cacheId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error("Failed to read cached avatar"));
    });
    if (!record?.blob) return null;
    if (record.expiresAt && record.expiresAt <= Date.now()) {
      await deleteById(cacheId);
      return null;
    }
    runTransaction("readwrite", (store, resolve, reject) => {
      const updateReq = store.put({
        ...record,
        lastAccessedAt: Date.now(),
      });
      updateReq.onsuccess = () => resolve(true);
      updateReq.onerror = () => reject(updateReq.error || new Error("Failed to touch avatar cache"));
    }).catch(() => {});
    return record.blob;
  } catch {
    return null;
  }
}

/**
 * @param {string} url
 * @param {Blob} blob
 * @param {{ ttlMs?: number }} [options]
 * @returns {Promise<boolean>}
 */
export async function setCachedAvatarBlob(url, blob, { ttlMs = AVATAR_CACHE_TTL_MS } = {}) {
  const cacheId = getAvatarCacheId(url);
  if (!cacheId || !blob) return false;
  const now = Date.now();
  const record = {
    id: cacheId,
    blob,
    size: blob.size || 0,
    contentType: blob.type || "image/jpeg",
    sourceUrl: url,
    cachedAt: now,
    lastAccessedAt: now,
    expiresAt: now + ttlMs,
  };
  try {
    const hasCapacity = await ensureCacheCapacity(record.size, cacheId);
    if (!hasCapacity) return false;
    await runTransaction("readwrite", (store, resolve, reject) => {
      const req = store.put(record);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error("Failed to store cached avatar"));
    });
    return true;
  } catch {
    return false;
  }
}
